import { useMemo } from 'react';
import styled from 'styled-components';

const tootipColors = {
  grey: { background: '#505363', text: '#FFFFFF' },
  white: { background: '#FFFFFF', text: '#232429' },
  primary: { background: '#196EFF', text: '#FFFFFF' },
  secondary: { background: '#FF3049', text: '#FFFFFF' },
  tertiary: { background: '#FF9500', text: '#FFFFFF' },
  quaternary: { background: '#34C77E', text: '#FFFFFF' },
} as const;

const tooltipSize = {
  small: { gap: '8px', fontSize: '10px' },
  medium: { gap: '10px', fontSize: '12px' },
  large: { gap: '16px', fontSize: '14px' },
};

type TooltipColorType = keyof typeof tootipColors;
type TooltipSize = keyof typeof tooltipSize;
type TooltipType = 'default' | 'narrow';
type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface CustomTooltipColor {
  background: string;
  text: string;
}

interface ITooltipCoordinates {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

interface TooltipProps {
  text: React.ReactNode;
  size: TooltipSize;
  type?: TooltipType;
  color?: TooltipColorType;
  customColor?: CustomTooltipColor;
  position?: TooltipPosition;
  children: React.ReactNode;
  style?: React.CSSProperties;
  visible?: boolean;
}

export const Tooltip = ({
  text,
  size,
  type = 'default',
  color = 'grey',
  customColor,
  position = 'top',
  children,
  style,
  visible = true,
}: TooltipProps) => {
  const isVertical: boolean = position === 'top' || position === 'bottom';

  const tooltipCoordinates = useMemo<ITooltipCoordinates>(() => {
    switch (position) {
      case 'top':
        return { bottom: 100, left: 50 };
      case 'bottom':
        return { top: 100, left: 50 };
      case 'left':
        return { top: 0, right: 100 };
      case 'right':
        return { top: 0, left: 100 };
      default:
        return { top: 0, bottom: 0, left: 0, right: 0 };
    }
  }, [position]);

  return (
    <TooltipWrapper>
      {children}
      {visible && (
        <TooltipBox isVertical={isVertical} tooltipCoordinates={tooltipCoordinates}>
          {position === 'bottom' && (
            <BottomTooltipTail fill={customColor?.background || tootipColors[color].background} />
          )}
          {position === 'right' && (
            <RightTooltipTail fill={customColor?.background || tootipColors[color].background} />
          )}

          {/* TODO: 틀팁내 badge 구현  */}
          <TooltipContent type={type} size={size} color={customColor?.background || tootipColors[color].background}>
            <TooltipLabel type={type} size={size} color={customColor?.text || tootipColors[color].text}>
              {text}
            </TooltipLabel>
          </TooltipContent>

          {position === 'top' && <TopTooltipTail fill={customColor?.background || tootipColors[color].background} />}
          {position === 'left' && <LeftTooltipTail fill={customColor?.background || tootipColors[color].background} />}
        </TooltipBox>
      )}
    </TooltipWrapper>
  );
};

const TopTooltipTail = ({ fill }: { fill: string }) => {
  return (
    <TooltipTail xmlns={'http://www.w3.org/2000/svg'} width={'8'} height={'6'} viewBox={'0 0 8 6'} fill={'none'}>
      <path
        d={
          'M5.24537e-07 -0.000733121L3.16795 4.75119C3.56377 5.34492 4.43623 5.34493 4.83205 4.75119L8 -0.000732422L5.24537e-07 -0.000733121Z'
        }
        fill={fill}
      />
    </TooltipTail>
  );
};

const BottomTooltipTail = ({ fill }: { fill: string }) => {
  return (
    <TooltipTail xmlns={'http://www.w3.org/2000/svg'} width={'8'} height={'6'} viewBox={'0 0 8 6'} fill={'none'}>
      <path
        d={'M5.24537e-07 6L3.16795 1.24808C3.56377 0.654343 4.43623 0.654342 4.83205 1.24808L8 6L5.24537e-07 6Z'}
        fill={fill}
      />
    </TooltipTail>
  );
};

const LeftTooltipTail = ({ fill }: { fill: string }) => {
  return (
    <TooltipTail xmlns={'http://www.w3.org/2000/svg'} width={'6'} height={'8'} viewBox={'0 0 6 8'} fill={'none'}>
      <path
        d={
          'M9.5399e-08 -7.15493e-08L4.75192 3.16795C5.34566 3.56377 5.34566 4.43623 4.75192 4.83205L0 8L9.5399e-08 -7.15493e-08Z'
        }
        fill={fill}
      />
    </TooltipTail>
  );
};

const RightTooltipTail = ({ fill }: { fill: string }) => {
  return (
    <TooltipTail xmlns={'http://www.w3.org/2000/svg'} width={'6'} height={'8'} viewBox={'0 0 6 8'} fill={'none'}>
      <path
        d={'M6 -7.15493e-08L1.24808 3.16795C0.654342 3.56377 0.654342 4.43623 1.24808 4.83205L6 8L6 -7.15493e-08Z'}
        fill={fill}
      />
    </TooltipTail>
  );
};

const TooltipWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;

  position: relative;
`;

const TooltipContent = styled.div<{ type: TooltipType; size: TooltipSize; color: string }>`
  display: flex;
  align-items: center;
  gap: ${(props) => tooltipSize[props.size].gap};

  padding: ${(props) => (props.type === 'narrow' ? '4px 8px' : '8px 16px')};

  border-radius: var(--radius-medium, 8px);
  background: ${(props) => props.color};
`;

const TooltipBox = styled.div<{ tooltipCoordinates: ITooltipCoordinates; isVertical: boolean }>`
  max-width: 300px;

  display: flex;
  flex-direction: ${(props) => (props.isVertical ? 'column' : 'row')};
  align-items: center;

  ${({ tooltipCoordinates }) => typeof tooltipCoordinates.top === 'number' && `top: ${tooltipCoordinates.top}%;`}
  ${({ tooltipCoordinates }) =>
    typeof tooltipCoordinates.bottom === 'number' && `bottom: ${tooltipCoordinates.bottom}%;`}
  ${({ tooltipCoordinates }) => typeof tooltipCoordinates.left === 'number' && `left: ${tooltipCoordinates.left}%;`}
  ${({ tooltipCoordinates }) => typeof tooltipCoordinates.right === 'number' && `right: ${tooltipCoordinates.right}%;`};

  ${({ isVertical }) => isVertical && 'transform: translateX(-50%);'}

  position: absolute;

  border-radius: var(--radius-medium, 8px);
`;

const TooltipLabel = styled.span<{ type: TooltipType; size: TooltipSize; color: string }>`
  white-space: nowrap;
  color: ${(props) => props.color};

  /* Display/X-Small/xs-bold */
  font-family: Pretendard;
  font-size: ${(props) => tooltipSize[props.size].fontSize};
  font-style: normal;
  font-weight: ${(props) => (props.type === 'narrow' ? 700 : 400)};
  line-height: 130%; /* 13px */
`;

const TooltipTail = styled.svg``;
